import { useCallback, useEffect, useState } from "react";
import { apiFetch, ApiRequestError } from "@/lib/api-client";

interface UseApiState<T> {
  data: T | null;
  isLoading: boolean;
  error: string | null;
}

/** Thin wrapper around `apiFetch` for the common "load this on mount,
 * expose loading/error state, allow a manual refetch" pattern every
 * dashboard page needs. Not a caching layer -- just avoids re-writing the
 * same three `useState` calls and `useEffect` in every route. */
export function useApi<T>(path: string | null, deps: unknown[] = []): UseApiState<T> & { refetch: () => void } {
  const [data, setData] = useState<T | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);

  const refetch = useCallback(() => setReloadToken((n) => n + 1), []);

  useEffect(() => {
    if (path === null) {
      setIsLoading(false);
      return;
    }
    let cancelled = false;
    setIsLoading(true);
    setError(null);

    apiFetch<T>(path)
      .then((result) => {
        if (!cancelled) setData(result);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err instanceof ApiRequestError ? err.message : "Something went wrong loading this data.");
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, reloadToken, ...deps]);

  return { data, isLoading, error, refetch };
}
